import { useMemo, useState } from 'react';
import { WINDOW_LABELS, WINDOW_URGENCY, type WindowStatus } from '../../audit/windows';
import { gradeOf } from '../../audit/grades';
import { byUrgency, earliestDue, type StoreRow } from '../../data/dashboard';
import { BOARD_CSV_HEADER, boardCsvRows } from '../../data/dashboardCsv';
import { withOwnerHeader } from '../../reports/ownerColumn';
import { useOwnerFilter } from '../reports/OwnerFilter';
import type { PublishedTemplate } from '../../data/templates';
import type { Store } from '../../data/types';
import { CsvButton } from '../CsvButton';
import { StoreSearchSelect } from '../StoreSearchSelect';
import { BoardTable, type GradesState } from './DashboardTables';

type BoardSort = 'urgency' | 'timeliness' | 'quality' | 'store' | 'nextDue' | 'tasks';

/**
 * The store board with its toolbar: status chips that filter, store search,
 * sort, group by district, "only overdue tasks", and the CSV of what it
 * shows. Moved out of HomePage (Home reorganisation, owner rulings
 * 2026-10-25): a DM's / store team's Home and the district pages (the
 * Reports "All stores" board was removed in the Reports redesign).
 */
export function StoreBoard({
  rows,
  stores,
  templates,
  gradesState,
  heading,
  csvName = 'stores',
  forStoreTeam = false,
}: {
  rows: StoreRow[];
  stores: readonly Store[];
  templates: readonly PublishedTemplate[] | null;
  gradesState: GradesState;
  heading: string;
  csvName?: string;
  forStoreTeam?: boolean;
}) {
  // The Owner column in the CSV (staff only; the owner FILTER is applied by the page — 2026-10-28).
  const ownerFilter = useOwnerFilter();
  // Board controls (same toolbar family as the Audits/Tasks lists).
  const [storeFilter, setStoreFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<WindowStatus | ''>('');
  const [overdueTasksOnly, setOverdueTasksOnly] = useState(false);
  const [sortBy, setSortBy] = useState<BoardSort>('urgency');
  const [groupByDistrict, setGroupByDistrict] = useState(false);
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const toggleCollapsed = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const filteredRows = useMemo(() => {
    if (rows === null) return null;
    const filtered = rows.filter((r) => {
      if (storeFilter && String(r.store.id) !== storeFilter) return false;
      if (statusFilter && r.status !== statusFilter) return false;
      if (overdueTasksOnly && r.overdueTasks === 0) return false;
      return true;
    });
    return [...filtered].sort((a, b) => {
      if ((sortBy === 'timeliness' || sortBy === 'quality') && gradesState === 'ready') {
        // Worst first; no grade last.
        return (
          (gradeOf(a.grade, sortBy) ?? Infinity) - (gradeOf(b.grade, sortBy) ?? Infinity) ||
          WINDOW_URGENCY[a.status] - WINDOW_URGENCY[b.status]
        );
      }
      if (sortBy === 'store') {
        return a.store.storeNumber.localeCompare(b.store.storeNumber, undefined, {
          numeric: true,
        });
      }
      if (sortBy === 'nextDue') {
        return (
          (earliestDue(a) ?? '9999').localeCompare(earliestDue(b) ?? '9999') ||
          WINDOW_URGENCY[a.status] - WINDOW_URGENCY[b.status]
        );
      }
      if (sortBy === 'tasks') {
        return (
          b.overdueTasks - a.overdueTasks ||
          b.openTasks - a.openTasks ||
          WINDOW_URGENCY[a.status] - WINDOW_URGENCY[b.status]
        );
      }
      return byUrgency(a, b);
    });
  }, [rows, storeFilter, statusFilter, overdueTasksOnly, sortBy, gradesState]);

  /** District sections (only when grouping is on and results span >1). */
  const groupedRows = useMemo(() => {
    if (!groupByDistrict || filteredRows === null) return null;
    const byDistrict = new Map<string, StoreRow[]>();
    for (const r of filteredRows) {
      const d = r.store.district.trim();
      const list = byDistrict.get(d) ?? [];
      list.push(r);
      byDistrict.set(d, list);
    }
    return [...byDistrict.entries()]
      .map(([district, districtRows]) => ({ district, rows: districtRows }))
      .sort((a, b) => (a.district || '\uffff').localeCompare(b.district || '\uffff'));
  }, [groupByDistrict, filteredRows]);

  const filtersActive =
    storeFilter !== '' || statusFilter !== '' || overdueTasksOnly;

  const counts = useMemo(() => {
    const c: Record<WindowStatus, number> = {
      overdue: 0,
      never: 0,
      due_soon: 0,
      current: 0,
    };
    for (const r of rows ?? []) c[r.status]++;
    return c;
  }, [rows]);

  const now = new Date();

  return (
        <>
          <div className="page-head dash-heading">
            <h2 style={{ margin: 0 }}>{heading}</h2>
            {/* What the board shows now: its filters and sort (grouping only changes the layout). */}
            {gradesState !== 'loading' && filteredRows !== null && filteredRows.length > 0 && (
              <CsvButton
                name={csvName}
                header={withOwnerHeader(BOARD_CSV_HEADER, ownerFilter.ownerOf)}
                rows={() => boardCsvRows(filteredRows, new Date(), gradesState === 'ready', ownerFilter.ownerOf)}
              />
            )}
          </div>
          {/* Status chips double as the audit-status filter. */}
          <div className="board-summary">
            {(['overdue', 'never', 'due_soon', 'current'] as const).map(
              (st) =>
                counts[st] > 0 && (
                  <button
                    key={st}
                    type="button"
                    className={`win-chip win-${st} chip-filter${statusFilter === st ? ' chip-on' : ''}`}
                    title={
                      statusFilter === st
                        ? 'Click to show all statuses'
                        : `Show only ${WINDOW_LABELS[st].toLowerCase()} stores`
                    }
                    onClick={() =>
                      setStatusFilter((cur) => (cur === st ? '' : st))
                    }
                  >
                    {counts[st]} {WINDOW_LABELS[st].toLowerCase()}
                  </button>
                ),
            )}
            {templates !== null && templates.length === 0 && (
              <span className="muted">
                No published audit templates yet — statuses appear once one is
                published.
              </span>
            )}
          </div>

          {rows.length > 1 && (
            <div className="list-tools">
              <StoreSearchSelect
                stores={[...stores]}
                value={storeFilter}
                onChange={setStoreFilter}
                placeholder="All stores"
              />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as BoardSort)}
                aria-label="Sort"
              >
                <option value="urgency">Sort: most urgent</option>
                <option value="timeliness">Sort: lowest timeliness</option>
                <option value="quality">Sort: lowest quality</option>
                <option value="store">Sort: store</option>
                <option value="nextDue">Sort: next due</option>
                <option value="tasks">Sort: most open tasks</option>
              </select>
              <label className="checkbox group-toggle">
                <input
                  type="checkbox"
                  checked={groupByDistrict}
                  onChange={(e) => setGroupByDistrict(e.target.checked)}
                />
                Group by district
              </label>
              <label className="checkbox group-toggle">
                <input
                  type="checkbox"
                  checked={overdueTasksOnly}
                  onChange={(e) => setOverdueTasksOnly(e.target.checked)}
                />
                Only overdue tasks
              </label>
              {filtersActive && (
                <button
                  className="btn btn-small"
                  onClick={() => {
                    setStoreFilter('');
                    setStatusFilter('');
                    setOverdueTasksOnly(false);
                  }}
                >
                  Clear filters
                </button>
              )}
            </div>
          )}

          {filteredRows !== null && filteredRows.length === 0 && (
            <p className="muted">No stores match these filters.</p>
          )}

          {groupedRows !== null
            ? groupedRows.map(({ district, rows: districtRows }) => {
                const key = `d:${district}`;
                const overdueCount = districtRows.filter(
                  (r) => r.status === 'overdue' || r.status === 'never',
                ).length;
                return (
                  <details
                    key={district || '(none)'}
                    className="task-district"
                    open={!collapsed.has(key)}
                  >
                    <summary
                      className="task-district-head"
                      onClick={(e) => {
                        e.preventDefault();
                        toggleCollapsed(key);
                      }}
                    >
                      {district || 'No district'}
                      <span className="muted"> ({districtRows.length})</span>
                      {overdueCount > 0 && (
                        <span className="win-chip win-overdue">
                          {overdueCount} behind
                        </span>
                      )}
                    </summary>
                    <BoardTable rows={districtRows} now={now} gradesState={gradesState} forStoreTeam={forStoreTeam} chromePx={240} />
                  </details>
                );
              })
            : filteredRows !== null &&
              filteredRows.length > 0 && (
                <BoardTable rows={filteredRows} now={now} gradesState={gradesState} forStoreTeam={forStoreTeam} chromePx={220} />
              )}
        </>
  );
}
